import type { MulticallRetryFunction } from '../../types'

/**
 * Probe amount for the share-price read. `convertToAssets(x)` is a pure
 * ratio (assets-per-share), so the result scales with `x` but the
 * *growth* ratio across two reads is independent of it — we just fix it
 * at 1e18 for every vault regardless of decimals.
 */
export const VAULT_SHARE_PRICE_PROBE = 10n ** 18n

const ConvertToAssetsAbi = [
  {
    name: 'convertToAssets',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ type: 'uint256', name: 'shares' }],
    outputs: [{ type: 'uint256', name: 'assets' }],
  },
] as const

/**
 * Read the current ERC-4626 share price (`convertToAssets(1e18)`) for a
 * batch of vaults in one multicall. The basic, protocol-agnostic yield
 * signal — every standard 4626 vault exposes it, so this works across
 * Morpho / Euler / Silo / Fluid / LST / savings / arbitrary 4626 vaults
 * alike.
 *
 * Returns a map of lowercased address → raw share price (string). Vaults
 * whose call reverts or returns 0 (non-4626, empty, or paused) are
 * omitted rather than throwing — the caller records what it can.
 *
 * This is the *realized* yield signal. See [./README.md](./README.md) for
 * how it ranks against a vault's *published* intrinsic APR (the published
 * fetcher wins at the display layer; this is the fallback / drift check).
 */
export const readVaultSharePrices = async (
  chainId: string,
  addresses: string[],
  multicallRetry: MulticallRetryFunction,
): Promise<Record<string, string>> => {
  const lc = Array.from(new Set(addresses.map((a) => a.toLowerCase())))
  if (lc.length === 0) return {}

  const results = (await multicallRetry({
    chain: chainId,
    calls: lc.map((address) => ({
      address,
      name: 'convertToAssets',
      params: [VAULT_SHARE_PRICE_PROBE],
    })),
    abi: ConvertToAssetsAbi as never,
    maxRetries: 3,
    allowFailure: true,
  })) as unknown[]

  const out: Record<string, string> = {}
  for (let i = 0; i < lc.length; i++) {
    const v = results[i]
    if (typeof v === 'bigint' && v > 0n) out[lc[i]] = v.toString()
  }
  return out
}
